import * as R from "ramda";
import * as React from "react";
import { FaTimes, FaTrash } from "react-icons/fa";
import styled from "styled-components";
import colors from "../../../../../../../styles/colors";
import { PreferredLocale } from "../../../../../../../utils/locales";
import { HTMLEditor } from "../../../../../../course-author/html-editor/Editor.container";
import { HTMLRenderer } from "../../../../../../course-author/html-editor/Renderer";
import {
  AnswerBox,
  ClickableText,
  HintBox,
} from "../../../../../../course-author/instruction/parts/exercises/FormatElements";
import { IClozeGapLocalized, ICourseLocalizedText } from "../../../../../../course-author/types";
import ClickableElement from "../../../../../../utils/ClickableElement";
import ToggleDisplay from "../../../../../../utils/ToggleDisplay";
import ActionConfirmationButton from "../../../../ActionConfirmation";
import { Input } from "../../../../FormElements";

export interface IEditGapsProps {
  currentLocale: PreferredLocale;
  gaps: IClozeGapLocalized[];
  templateLength: number;
  deleteGap: (gapId: number) => () => void;
  modifyClozeGapAnswers: (
    gapId: number,
    answerId: number,
    oldAnswers: string[],
    newAnswers: string[],
    currentLocale: PreferredLocale
  ) => () => void;
  modifyClozeGapHint: (
    gapId: number,
    hintId: number,
    oldHint: string,
    newHint: string,
    currentLocale: PreferredLocale
  ) => () => void;
}

export interface IEditGapsState {
  editGapHint: {
    [key: number]: boolean;
  };
}

const RGContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 2rem;

  span {
    color: ${colors.red};
  }
`;

const AnswerContent = styled.div`
  border: 1px solid ${colors.darkgrey};
  border-radius: 0.5rem;
  margin: 0.5rem;
  display: flex;
  height: 4rem;
  align-items: center;
  padding-right: 1rem;
`;

interface IRedundantGapProps {
  deleteGap: (id: number) => () => void;
  id: number;
}

const RedundantGap: React.FunctionComponent<IRedundantGapProps> = props => (
  <RGContainer>
    <span>This gap is redundant</span>
    <ActionConfirmationButton ActionIcon={FaTrash} action="Delete" actionFn={props.deleteGap(props.id)} />
  </RGContainer>
);

interface IEditGapHintProps {
  edit: boolean;
  gap: IClozeGapLocalized;
  hint?: ICourseLocalizedText;
  getHint: () => string;
  toggleEditHint: (gapId: number) => () => void;
  saveHint: (
    gapId: number,
    hintId: number,
    currentHintText: string
  ) => (content: string, currentLocale?: PreferredLocale) => void;
  hintText: string;
  locale: PreferredLocale;
}

const EditGapHint: React.FunctionComponent<IEditGapHintProps> = props => {
  const containerRef = React.useRef() as React.RefObject<HTMLDivElement>;

  return (
    <HintBox ref={containerRef}>
      <ClickableText shouldDisplay={!props.edit} onClick={props.toggleEditHint(props.gap.id)}>
        <HTMLRenderer source={props.hintText} />
      </ClickableText>
      <ToggleDisplay shouldDisplay={props.edit}>
        <HTMLEditor
          getContent={props.getHint}
          setContent={props.saveHint(props.gap.id, props.hint ? props.hint.id : -1, props.hintText)}
          currentLocale={props.locale}
          shouldAutoFocus={true}
          containerRef={containerRef}
        />
      </ToggleDisplay>
    </HintBox>
  );
};

interface IGapAnswerProps {
  answerId: number;
  answerIdx: number;
  currentLocale: PreferredLocale;
  gapId: number;
  modifyClozeGapAnswers: IEditGapsProps["modifyClozeGapAnswers"];
  options: string[];
}

const GapAnswer: React.FunctionComponent<IGapAnswerProps> = props => (
  <AnswerContent>
    <ClickableElement
      onClick={props.modifyClozeGapAnswers(
        props.gapId,
        props.answerId,
        props.options,
        props.options.filter((_, idx) => idx !== props.answerIdx),
        props.currentLocale
      )}
    >
      <FaTimes />
    </ClickableElement>
    {props.options[props.answerIdx]}
  </AnswerContent>
);

interface IEditGapAnswersProps {
  gap: IClozeGapLocalized;
  inputRef: React.RefObject<HTMLInputElement>;
  submitNewGapAnswer: (
    ref: React.RefObject<HTMLInputElement>,
    gapId: number,
    answerId: number,
    oldAnswers: string[]
  ) => React.KeyboardEventHandler;
  currentLocale: PreferredLocale;
  modifyClozeGapAnswers: IEditGapsProps["modifyClozeGapAnswers"];
}

const EditGapAnswers: React.FunctionComponent<IEditGapAnswersProps> = props => (
  <AnswerBox>
    {props.gap.answers.map(({ id: answerId, value: { value } }) => (
      <React.Fragment key={answerId}>
        {value.map((_, i) => (
          <GapAnswer
            key={i}
            currentLocale={props.currentLocale}
            modifyClozeGapAnswers={props.modifyClozeGapAnswers}
            options={value}
            answerIdx={i}
            answerId={answerId}
            gapId={props.gap.id}
          />
        ))}
        <Input
          ref={props.inputRef}
          onKeyDown={props.submitNewGapAnswer(props.inputRef, props.gap.id, answerId, value)}
        />
      </React.Fragment>
    ))}
    {R.isEmpty(props.gap.answers) && (
      <Input ref={props.inputRef} onKeyDown={props.submitNewGapAnswer(props.inputRef, props.gap.id, -1, [])} />
    )}
  </AnswerBox>
);

export class EditGaps extends React.Component<IEditGapsProps, IEditGapsState> {
  constructor(props: IEditGapsProps) {
    super(props);
    this.state = {
      editGapHint: props.gaps.reduce((editGapHint, { id }) => ({ ...editGapHint, [id]: false }), {}),
    };
  }

  public toggleEditHint = (hintIdx: number) => () =>
    this.setState({
      editGapHint: {
        ...this.state.editGapHint,
        [hintIdx]: !this.state.editGapHint[hintIdx],
      },
    });

  public submitNewGapAnswer = (
    ref: React.RefObject<HTMLInputElement>,
    gapId: number,
    answerId: number,
    oldAnswers: string[]
  ) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === "Escape") {
      if (!ref.current) {
        return;
      }

      const newAnswer = ref.current.value;

      if (newAnswer === "" || oldAnswers.indexOf(newAnswer) > 0) {
        return;
      }

      this.props.modifyClozeGapAnswers(
        gapId,
        answerId,
        oldAnswers,
        [...oldAnswers, newAnswer],
        this.props.currentLocale
      )();
      ref.current.value = "";
    }
  };

  public getHint = (gap: IClozeGapLocalized) => () => R.pathOr("", ["hint", 0, "value", "text"], gap);

  public saveHint = (gapId: number, hintId: number, oldHint: string) => (hint: string) => {
    if (hint !== oldHint) {
      this.props.modifyClozeGapHint(gapId, hintId, oldHint, hint, this.props.currentLocale)();
    }
    this.toggleEditHint(gapId)();
  };

  public render() {
    const { deleteGap, gaps, modifyClozeGapAnswers, templateLength } = this.props;

    return (
      <ol>
        {gaps.map((gap, idx) => {
          const inputRef: React.RefObject<HTMLInputElement> = React.createRef();
          const hint = R.head<ICourseLocalizedText>(gap.hint);
          const hintText: string = R.pathOr("", ["value", "text"], hint);
          return (
            <li key={gap.id}>
              <EditGapHint
                edit={this.state.editGapHint[gap.id]}
                toggleEditHint={this.toggleEditHint}
                getHint={this.getHint(gap)}
                saveHint={this.saveHint}
                hint={hint}
                hintText={hintText}
                gap={gap}
                locale={this.props.currentLocale}
              />
              <EditGapAnswers
                currentLocale={this.props.currentLocale}
                submitNewGapAnswer={this.submitNewGapAnswer}
                inputRef={inputRef}
                modifyClozeGapAnswers={modifyClozeGapAnswers}
                gap={gap}
              />
              {idx < templateLength ? null : <RedundantGap deleteGap={deleteGap} id={gap.id} />}
            </li>
          );
        })}
      </ol>
    );
  }
}
